import React, { useCallback, useEffect, useState } from "react";
import StakingService from "../../../api/services/StakingService";
import { resolvePublicUploadUrl } from "../../../api/apiConfig/apiConfig";
import { parseDec } from "./stakingStakeHelpers";
import "./staking.css";

const formatAmount = (val, decimals = 8) => {
  const n = parseFloat(parseDec(val));
  if (!Number.isFinite(n)) return "0";
  return n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: decimals });
};

const formatDateTime = (dateStr) => {
  if (!dateStr) return "—";
  const d = new Date(dateStr);
  if (Number.isNaN(d.getTime())) return dateStr;
  return d.toLocaleString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const formatPayoutType = (type) => {
  if (!type) return "—";
  return String(type).replace(/_/g, " ");
};

const resolveIcon = (iconPath, currency) => {
  if (!iconPath) {
    return `/images/staking_img/${String(currency || "usdt").toLowerCase()}_icon.svg`;
  }
  return resolvePublicUploadUrl(iconPath)
    || `/images/staking_img/${String(currency || "usdt").toLowerCase()}_icon.svg`;
};

const DetailRow = ({ label, value, highlight }) => (
  <div className="stake-overview-row">
    <span className="stake-overview-row__label">{label}</span>
    <span className={`stake-overview-row__value${highlight ? " stake-overview-row__value--green" : ""}`}>
      {value}
    </span>
  </div>
);

const StakingPayoutHistoryModal = ({ positionId, currency: fallbackCurrency, onClose }) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [positionInfo, setPositionInfo] = useState(null);
  const [payoutHistory, setPayoutHistory] = useState([]);
  const [pagination, setPagination] = useState({ page: 1, limit: 10, total: 0, totalPages: 1 });

  const fetchPayoutHistory = useCallback(async (page = 1) => {
    if (!positionId) return;
    setLoading(true);
    setError("");
    try {
      const res = await StakingService.getPayoutHistory(positionId, page, 10);
      if (res?.success) {
        setPositionInfo(res.data?.position || null);
        setPayoutHistory(Array.isArray(res.data?.payoutHistory) ? res.data.payoutHistory : []);
        if (res.pagination) {
          setPagination((prev) => ({ ...prev, ...res.pagination, page }));
        }
      } else {
        setError(res?.message || "Failed to load payout history.");
        setPositionInfo(null);
        setPayoutHistory([]);
      }
    } catch {
      setError("Something went wrong. Please try again.");
      setPositionInfo(null);
      setPayoutHistory([]);
    } finally {
      setLoading(false);
    }
  }, [positionId]);

  useEffect(() => {
    document.body.style.overflow = "hidden";
    document.documentElement.style.overflow = "hidden";
    const onKey = (e) => { if (e.key === "Escape") onClose?.(); };
    window.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = "";
      document.documentElement.style.overflow = "";
      window.removeEventListener("keydown", onKey);
    };
  }, [onClose]);

  useEffect(() => {
    fetchPayoutHistory(1);
  }, [fetchPayoutHistory]);

  const currency = positionInfo?.currency || fallbackCurrency || "";
  const rewardCurrency = positionInfo?.rewardCurrency || currency;
  const pkg = positionInfo?.package || {};
  const icon = resolveIcon(positionInfo?.rewardCurrencyIcon, currency);

  return (
    <div className="stake-overview-overlay" onClick={onClose}>
      <div className="stake-overview-content stake-payout-modal" onClick={(e) => e.stopPropagation()}>
        <div className="stake-overview-header">
          <div className="stake-overview-header__left">
            <div className="stake-overview-title">
              <img
                src={icon}
                alt={currency}
                className="mint-title-icon"
                onError={(e) => {
                  e.target.onerror = null;
                  e.target.src = `/images/staking_img/${String(currency || "usdt").toLowerCase()}_icon.svg`;
                }}
              />
              <span>{currency ? `${currency} ` : ""}Payout History</span>
            </div>
          </div>
          <button type="button" className="mint-modal-close" onClick={onClose}>
            <i className="ri-close-line" />
          </button>
        </div>

        {loading ? (
          <div className="stake-payout-modal__status">Loading...</div>
        ) : error ? (
          <div className="stake-payout-modal__status stake-payout-modal__status--error">{error}</div>
        ) : (
          <>
            {positionInfo ? (
              <div className="stake-overview-body stake-payout-modal__summary">
                <DetailRow label="Status" value={positionInfo.status || "—"} />
                <DetailRow
                  label="Total Invested"
                  value={`${formatAmount(positionInfo.totalInvestedAmount ?? positionInfo.investedAmount)} ${currency}`}
                />
                <DetailRow
                  label="Total Reward Earned"
                  value={`${formatAmount(positionInfo.totalRewardEarned)} ${rewardCurrency}`}
                  highlight
                />
                <DetailRow
                  label="Yesterday Return"
                  value={`${formatAmount(positionInfo.yesterdayReturn)} ${rewardCurrency}`}
                  highlight
                />
                <DetailRow
                  label="Daily Reward"
                  value={`${formatAmount(positionInfo.dailyReward)} ${rewardCurrency}`}
                />
                <DetailRow
                  label="APR"
                  value={`${positionInfo.aprAtSubscription ?? pkg.returnPercentage ?? "—"}%`}
                />
                <DetailRow
                  label="Duration"
                  value={pkg.duration != null ? `${pkg.duration} ${pkg.durationType || "Days"}` : "—"}
                />
              </div>
            ) : null}

            <div className="stake-payout-modal__records">
              <div className="stake-payout-modal__recordsTitle">Payout Records</div>
              {payoutHistory.length === 0 ? (
                <div className="stake-payout-modal__empty">No payout history found.</div>
              ) : (
                <div className="stake-payout-modal__list">
                  {payoutHistory.map((item) => (
                    <div key={item.payoutId || `${item.paidAt}-${item.amount}`} className="stake-payout-modal__record">
                      <div className="stake-payout-modal__recordTop">
                        <span className="stake-payout-modal__recordAmount">
                          +{formatAmount(item.amount)} {item.currency || rewardCurrency}
                        </span>
                        <span className="stake-payout-modal__recordType">{formatPayoutType(item.type)}</span>
                      </div>
                      <div className="stake-payout-modal__recordBottom">
                        <span>{formatDateTime(item.paidAt || item.createdAt)}</span>
                        <span>{item.creditedBy || "—"}</span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {pagination.totalPages > 1 ? (
              <div className="staking_pagination stake-payout-modal__pagination">
                <button
                  type="button"
                  className="page_nav prev_nav"
                  disabled={pagination.page <= 1}
                  onClick={() => fetchPayoutHistory(pagination.page - 1)}
                >
                  ‹
                </button>
                {Array.from({ length: pagination.totalPages }, (_, i) => i + 1).map((p) => (
                  <button
                    key={p}
                    type="button"
                    className={`page_num${pagination.page === p ? " active" : ""}`}
                    onClick={() => fetchPayoutHistory(p)}
                  >
                    {p}
                  </button>
                ))}
                <button
                  type="button"
                  className="page_nav next_nav"
                  disabled={pagination.page >= pagination.totalPages}
                  onClick={() => fetchPayoutHistory(pagination.page + 1)}
                >
                  ›
                </button>
              </div>
            ) : null}
          </>
        )}

        <div className="stake-overview-actions stake-payout-modal__actions">
          <button type="button" className="mint-btn-confirm stake-success-btn" onClick={onClose}>
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default StakingPayoutHistoryModal;
